import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema, caseStudySchema } from "../lib/seo";

const PaymentsCanadaCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/case-studies/payments-canada")}
        schema={[
          caseStudySchema("/case-studies/payments-canada"),
          breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: "Payments Canada", path: "/case-studies/payments-canada" },
        ]),
        ]}
      />
      {/* Page Title Bar */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <Link
                to="/case-studies"
                className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
                data-testid="back-to-case-studies-link"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Case Studies
              </Link>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Payments Canada: Electronic Drink Tickets
              </h1>
            </div>
            <div className="lg:col-span-3">
              <div
                className="w-full h-72 md:h-96 rounded-lg bg-cover bg-center shadow-2xl"
                style={{
                  backgroundImage:
                    "url('/images/case-studies/top-performers-wristcoin.png')",
                }}
                role="img"
                aria-label="Payments Canada Event"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Challenges</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Payments Canada is an annual payments conference, an industry event where the latest payment technologies and trends are shared. Attendees are provided with meals and two complimentary alcoholic beverages during evening networking gatherings. Paper drink tickets would not create the desired experience for the guests who are all payment industry experts. The event producers also wanted an accurate tally of beverage consumption so the final bill is correct. The catering company often experienced inaccurate and delayed ticket tallies causing billing disputes with no records available to resolve them objectively. Furthermore, event producers wanted some VIP guests to be discreetly provided unlimited drinks without having a VIP carry a roll of drink tickets.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Solution</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              WristCoin wallets are loadable and pre-loadable with various forms of credit for monetary use cases and those for points, tickets, or rewards. All Payments Canada wristbands were handed out to guests pre-loaded with the two beverage tickets that could be redeemed for any drink being served. The catering team was able to customize the menu to the exact list of drinks.
            </p>

            <img
              src="/images/case-studies/inventory-management-wristcoin.png"
              alt="WristCoin Inventory Management"
              className="w-full rounded-lg shadow-2xl mb-6"
            />

            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Because all the wristbands were pre-loaded with the tickets, guests were able to just go straight to the bar and order without having to locate any drink tickets given to them earlier. With only a half-hour of training, the bar staff was able to become fast and proficient using WristCoin mobile point of sale terminals. Although no WiFi was needed to support live payments, the conference WiFi was sufficient to allow real-time reporting of vending statistics:
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Guests could have topped up with additional credit for more drink purchases if they wanted to. This way, the organizer was able to demonstrate that any overconsumption was the result of the guest's purchases and not the complimentary bar service.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Outcome</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Guests enjoyed the experience without having to handle and potentially lose drink tickets while VIP was discreetly provided a more lavish experience. At the end of the event, mere minutes after the last drink was served, a complete and up to date drink tally was available through the WristCoin administration portal, and the report was easily exported to Excel so an accurate bill was presented to the event producer in a matter of minutes.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default PaymentsCanadaCaseStudy;
